import React, { useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";


import { getOverdueInvoices } from "@/data/demo-database";
import { InfoTooltip } from "@/components/tours/InfoTooltip";
import { DEMO_BANK_ACCOUNTS } from "@/data/demo-data";
import type {
  OverdueInvoiceData,
} from "@/hooks/useDrillDown";
import { CashBreakdownModal } from "@/components/modals/CashBreakdownModal";
import { usePermissions } from "@/hooks/usePermissions";
import { useFormatting } from "@/hooks/useFormatting";
import { useApp } from "@/contexts/AppContext";
import { useDashboardMetrics } from "@/hooks/useDashboardMetrics";
import type { BankAccount } from "@/types";


// ─── Overdue Invoices Table ───────────────────────────────────────────────────

const CONTENT_COLOR = 'oklch(0.2764 0.021 248.59)';
const TEAL_ACTIVE   = 'oklch(0.5 0.0896 194.8)';

type OvdInvoiceRow = {
  id: string;
  invoiceNumber: string | null;
  customer: string;
  dueDate: Date;
  amount: number;
  daysOverdue: number;
};

type OvdSortKey = 'customer' | 'invoiceNumber' | 'dueDate' | 'daysOverdue' | 'amount';
type OvdSortDir = 'asc' | 'desc' | 'none';

const OVD_COLS: { key: OvdSortKey; label: string; minWidth: number; align?: 'right' }[] = [
  { key: 'customer',      label: 'Customer',      minWidth: 140 },
  { key: 'invoiceNumber', label: 'Invoice #',     minWidth: 110 },
  { key: 'dueDate',       label: 'Due Date',      minWidth: 110 },
  { key: 'daysOverdue',   label: 'Days Overdue',  minWidth: 110 },
  { key: 'amount',        label: 'Amount',        minWidth: 100, align: 'right' },
];

function OverdueInvoicesTable({
  invoices,
  formatCurrency,
}: {
  invoices: OvdInvoiceRow[];
  // biome-ignore lint/suspicious/noExplicitAny: matches useFormatting signature
  formatCurrency: (v: number, ...args: any[]) => string;
}) {
  const PAGE_SIZE = 10;
  const [sortKey, setSortKey] = useState<OvdSortKey | null>(null);
  const [sortDir, setSortDir] = useState<OvdSortDir>('none');
  const [colWidths, setColWidths] = useState<Record<string, number>>({});
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);
  const [hoveredHeader, setHoveredHeader] = useState<string | null>(null);
  const [resizingCol, setResizingCol] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  function cycleSort(key: OvdSortKey) {
    setPage(1);
    if (sortKey !== key) { setSortKey(key); setSortDir('desc'); return; }
    if (sortDir === 'desc') { setSortDir('asc'); return; }
    setSortKey(null); setSortDir('none');
  }

  const sorted = useMemo(() => {
    if (!sortKey || sortDir === 'none') return invoices;
    return [...invoices].sort((a, b) => {
      const av = a[sortKey]; const bv = b[sortKey];
      let cmp = 0;
      if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv;
      else if (av instanceof Date && bv instanceof Date) cmp = av.getTime() - bv.getTime();
      else cmp = String(av).localeCompare(String(bv));
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [invoices, sortKey, sortDir]);

  const totalPages = Math.ceil(sorted.length / PAGE_SIZE);
  const paginated = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  function startResize(e: React.MouseEvent, key: string) {
    e.preventDefault();
    const startX = e.clientX;
    const startW = colWidths[key] ?? OVD_COLS.find(c => c.key === key)?.minWidth ?? 100;
    setResizingCol(key);
    const doc = e.currentTarget.ownerDocument;
    const onMove = (ev: MouseEvent) => {
      const newW = Math.max(40, startW + ev.clientX - startX);
      setColWidths(prev => ({ ...prev, [key]: newW }));
    };
    const onUp = () => {
      setResizingCol(null);
      doc.removeEventListener('mousemove', onMove);
      doc.removeEventListener('mouseup', onUp);
    };
    doc.addEventListener('mousemove', onMove);
    doc.addEventListener('mouseup', onUp);
  }

  return (
    <div style={{ overflowX: 'auto', minWidth: 0 }}>
      <table style={{ tableLayout: 'fixed', width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: '#ffffff' }}>
            {OVD_COLS.map(col => {
              const isActive = sortKey === col.key;
              const w = colWidths[col.key] ?? col.minWidth;
              const isHov = hoveredHeader === col.key;
              return (
                <th
                  key={col.key}
                  style={{
                    width: w,
                    position: 'relative',
                    overflow: 'visible',
                    padding: '8px 12px',
                    fontWeight: 600,
                    fontSize: 14,
                    color: isActive ? TEAL_ACTIVE : CONTENT_COLOR,
                    textAlign: 'left',
                    userSelect: 'none',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                  onMouseEnter={() => setHoveredHeader(col.key)}
                  onMouseLeave={() => setHoveredHeader(null)}
                  onClick={() => cycleSort(col.key)}
                >
                  <span style={{ color: isActive || isHov ? TEAL_ACTIVE : CONTENT_COLOR }}>
                    {col.label}
                    {isActive && sortDir === 'desc' && ' ↓'}
                    {isActive && sortDir === 'asc'  && ' ↑'}
                  </span>
                  {/* Resize handle */}
                  <span
                    style={{
                      position: 'absolute', right: -4, top: 0,
                      height: '100%', width: 8, zIndex: 10,
                      cursor: 'col-resize', display: 'flex',
                      alignItems: 'stretch', justifyContent: 'center',
                    }}
                    onClick={e => e.stopPropagation()}
                    onMouseDown={e => startResize(e, col.key)}
                  >
                    <span style={{
                      width: 2,
                      background: resizingCol === col.key ? TEAL_ACTIVE : 'transparent',
                      transition: 'background 0.1s',
                    }} />
                  </span>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {paginated.map((inv, index) => {
            const bg = hoveredRow === inv.id ? '#e5e7eb' : index % 2 === 0 ? '#ffffff' : '#f9fafb';
            return (
              <tr
                key={inv.id}
                style={{ backgroundColor: bg, cursor: 'default' }}
                onMouseEnter={() => setHoveredRow(inv.id)}
                onMouseLeave={() => setHoveredRow(null)}
              >
                <td style={{ padding: '8px 12px', fontSize: 14, color: CONTENT_COLOR, width: colWidths['customer'] ?? 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inv.customer}</td>
                <td style={{ padding: '8px 12px', fontSize: 14, color: CONTENT_COLOR, width: colWidths['invoiceNumber'] ?? 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inv.invoiceNumber}</td>
                <td style={{ padding: '8px 12px', fontSize: 14, color: CONTENT_COLOR, width: colWidths['dueDate'] ?? 110 }}>{inv.dueDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                <td style={{ padding: '8px 12px', fontSize: 14, color: CONTENT_COLOR, width: colWidths['daysOverdue'] ?? 110 }}>{inv.daysOverdue} days</td>
                <td style={{ padding: '8px 12px', fontSize: 14, fontWeight: 500, textAlign: 'right', color: 'var(--mc-error-icon)', width: colWidths['amount'] ?? 100 }}>{formatCurrency(inv.amount, 'full')}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', borderTop: '1px solid #e5e7eb', fontSize: 13, color: CONTENT_COLOR }}>
          <span>{(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, sorted.length)} of {sorted.length}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              type="button"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid #e5e7eb', background: page === 1 ? '#f3f4f6' : '#ffffff', color: page === 1 ? '#9ca3af' : CONTENT_COLOR, cursor: page === 1 ? 'default' : 'pointer', fontSize: 13 }}
            >Previous</button>
            <span>Page {page} of {totalPages}</span>
            <button
              type="button"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid #e5e7eb', background: page === totalPages ? '#f3f4f6' : '#ffffff', color: page === totalPages ? '#9ca3af' : CONTENT_COLOR, cursor: page === totalPages ? 'default' : 'pointer', fontSize: 13 }}
            >Next</button>
          </div>
        </div>
      )}
    </div>
  );
}


export default function ExecutiveCashPositionCard() {
  const { hasPermission } = usePermissions();
  const { formatCurrency } = useFormatting();
  const { mode } = useApp();
  const [showCashModal, setShowCashModal] = useState(false);

  const {
    metrics,
    isLoading,
    error,
    bankAccounts: liveBankAccountsApi,
    salesTransactions,
  } = useDashboardMetrics();

  // Derive overdue invoices for the table from raw sales transactions (live mode)
  const liveOverdueInvoices = useMemo((): OverdueInvoiceData[] => {
    if (mode !== "live") return [];
    const now = new Date();
    return salesTransactions
      .filter((txn) => {
        if (txn.transactionType?.toLowerCase() !== 'invoice') return false;
        if ((txn.outstanding ?? 0) <= 0) return false;
        const dueDate = new Date(txn.dueDate ?? txn.transactionDate ?? "");
        return dueDate < now;
      })
      .map((txn) => ({
        customer: txn.accountName ?? "",
        description: txn.transactionDescription ?? "",
        dueDate: txn.dueDate ?? "",
        outstandingValue: txn.outstanding ?? 0,
      }));
  }, [mode, salesTransactions]);

  // Map bank accounts for the breakdown modal (live mode)
  const liveBankAccounts = useMemo((): BankAccount[] => {
    if (mode !== "live") return [];
    return liveBankAccountsApi.map((account) => ({
      id: String(account.id),
      name: account.name,
      bank: account.bank,
      balance: account.balance,
      currency: account.currencyCode,
      type: account.accountType ?? undefined,
    }));
  }, [mode, liveBankAccountsApi]);

  if (!hasPermission("view_executive_cash_position_card")) {
    return null;
  }

  const bankAccountsForModal = mode === "live" ? liveBankAccounts : DEMO_BANK_ACCOUNTS;

  // Show loading state in live mode
  if (mode === "live" && isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <div className="text-muted-foreground">Loading overdue invoices...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show error state in live mode
  if (mode === "live" && error) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-destructive">{error}</div>
        </CardContent>
      </Card>
    );
  }

  // Safety check - if metrics calculation fails, show error
  if (!metrics) {
    console.error("CEO Dashboard - Failed to calculate metrics");
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-red-500">Error loading dashboard metrics. Please refresh the page.</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
        <Card>
          <div className="flex items-center justify-between border-b px-4 py-3">
            <div className="flex items-center gap-1">
              <span className="text-lg font-semibold text-neutral-900">Overdue Invoices</span>
              <InfoTooltip
                title="Overdue Invoices"
                content="Customer invoices that are past their due date and have not yet been paid. Click any row for full details."
              />
            </div>

          </div>
          <CardContent className="space-y-4">
            {/* Additional Information — above the table */}
            {metrics.overdueInvoices > 0 && (() => {
              const invoices = mode === "live"
                ? liveOverdueInvoices.map((inv) => ({ daysOverdue: Math.floor((new Date().getTime() - new Date(inv.dueDate).getTime()) / (1000 * 60 * 60 * 24)), amount: inv.outstandingValue }))
                : getOverdueInvoices().map((inv) => ({ daysOverdue: inv.daysOverdue ?? 0, amount: inv.amount }));
              const avgDaysOverdue = invoices.length > 0 ? Math.round(invoices.reduce((sum, inv) => sum + inv.daysOverdue, 0) / invoices.length) : 0;
              const oldestInvoice = invoices.length > 0 ? Math.max(...invoices.map((inv) => inv.daysOverdue)) : 0;
              return (
                <div className="grid grid-cols-4 gap-4 border-b pb-4">
                  <div>
                    <p className="text-base font-semibold text-neutral-900">{invoices.length} invoices</p>
                    <p className="text-sm text-neutral-500">Total Invoices</p>
                  </div>
                  <div>
                    <p className="text-base font-semibold text-neutral-900">{oldestInvoice} days overdue</p>
                    <p className="text-sm text-neutral-500">Oldest Invoice</p>
                  </div>
                  <div>
                    <p className="text-base font-semibold text-neutral-900">{avgDaysOverdue} days</p>
                    <p className="text-sm text-neutral-500">Average Days Overdue</p>
                  </div>
                  <div>
                    <p className="text-base font-semibold text-neutral-900">{formatCurrency(metrics.overdueAmount, 'rounded')}</p>
                    <p className="text-sm text-neutral-500">Total Outstanding</p>
                  </div>
                </div>
              );
            })()}
            {/* Overdue Invoices Table */}
            {metrics.overdueInvoices > 0 && (
              <OverdueInvoicesTable
                invoices={mode === "live"
                  ? liveOverdueInvoices.map((inv, i) => ({
                      id: String(i),
                      invoiceNumber: inv.description,
                      customer: inv.customer,
                      dueDate: new Date(inv.dueDate),
                      amount: inv.outstandingValue,
                      daysOverdue: Math.floor((new Date().getTime() - new Date(inv.dueDate).getTime()) / (1000 * 60 * 60 * 24)),
                    }))
                  : getOverdueInvoices().map((inv) => ({
                      id: inv.id,
                      invoiceNumber: inv.invoiceNumber,
                      customer: inv.customer,
                      dueDate: inv.dueDate instanceof Date ? inv.dueDate : new Date(inv.dueDate),
                      amount: inv.amount,
                      daysOverdue: inv.daysOverdue ?? 0,
                    }))
                }
                formatCurrency={formatCurrency}
              />
            )}
          </CardContent>
        </Card>

      <CashBreakdownModal
        open={showCashModal}
        onClose={() => setShowCashModal(false)}
        accounts={bankAccountsForModal}
        totalCash={metrics.currentCash}
      />


    </>
  );
}
